import React, { useState, useEffect, useRef } from 'react';
import Map, { Marker, Popup } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MarkerIcon from '../../assets/images/fishicon3.png';
import SelectedMarkerIcon from '../../assets/images/fishicon2.png';
import './MapView.css';
import DataTable from 'react-data-table-component';

const MapView = ({ data }) => {
  const [viewport, setViewport] = useState({
    latitude: 7.2,
    longitude: 80.4194,
    zoom: 2,
  });
  const [selectedMarker, setSelectedMarker] = useState(null);
  const mapRef = useRef(null);

  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  // Determine map style based on local time
  const getMapStyle = () => {
    const hour = new Date().getHours();
    // Nighttime: 6 PM (18:00) to 6 AM (06:00)
    return hour >= 18 || hour < 6
      ? 'mapbox://styles/mapbox/dark-v10'
      : 'mapbox://styles/mapbox/streets-v11';
  };

  const [mapStyle, setMapStyle] = useState(getMapStyle());

  // Update map style when component mounts
  useEffect(() => {
    setMapStyle(getMapStyle());
  }, []);

  // Handle row selection change
  const handleSelectedRowsChange = ({ selectedRows }) => {
    setSelectedItems(selectedRows);
    const newSelectedId = selectedRows.length > 0 ? selectedRows[0]._id : null;
    setSelectedId(newSelectedId);
    // Sync selectedMarker with selectedId
    setSelectedMarker(data.find((item) => item._id === newSelectedId) || null);
  };

  const columns = [
    {
      name: 'QR Code',
      selector: (row) => row.qrCode || 'N/A',
      sortable: true,
    },
    {
      name: 'Catch Location',
      selector: (row) => row.catchLocation || 'N/A',
      sortable: true,
    },
    {
      name: 'Catch Date',
      selector: (row) =>
        row.catchDatetime
          ? new Date(row.catchDatetime).toLocaleDateString()
          : 'N/A',
      sortable: true,
    },
    {
      name: 'Weight',
      selector: (row) => row.weight || 'N/A',
      sortable: true,
    },
    {
      name: 'Quality',
      selector: (row) => row.quality || 'N/A',
      sortable: true,
    },
  ];

  // Parse lat/long from catchLocation
  const parseLocation = (location) => {
    if (!location || typeof location !== 'string') {
      return null;
    }
    const latMatch = location.match(/Lat:\s*([\d.-]+)/i);
    const longMatch = location.match(/Long:\s*([\d.-]+)/i);
    const latitude = latMatch ? parseFloat(latMatch[1]) : null;
    const longitude = longMatch ? parseFloat(longMatch[1]) : null;

    if (
      latitude === null ||
      longitude === null ||
      isNaN(latitude) ||
      isNaN(longitude) ||
      latitude < -90 ||
      latitude > 90 ||
      longitude < -180 ||
      longitude > 180
    ) {
      return null;
    }

    return { latitude, longitude };
  };

  // Handle marker click
  const handleMarkerClick = (item) => {
    setSelectedMarker(item);
    setSelectedId(item._id);
    setSelectedItems([item]); // Sync DataTable selection
    if (mapRef.current && item.catchLocation) {
      const location = parseLocation(item.catchLocation);
      if (location) {
        mapRef.current.flyTo({
          center: [location.longitude, location.latitude],
          zoom: 6,
          duration: 1000,
        });
      }
    }
  };

  // Center map on valid locations when data changes
  useEffect(() => {
    if (!mapRef.current || !data?.length) return;

    const validLocations = data
      .map((item) => parseLocation(item.catchLocation))
      .filter((loc) => loc !== null);

    if (validLocations.length > 0) {
      const avgLat =
        validLocations.reduce((sum, loc) => sum + loc.latitude, 0) /
        validLocations.length;
      const avgLong =
        validLocations.reduce((sum, loc) => sum + loc.longitude, 0) /
        validLocations.length;
      mapRef.current.flyTo({
        center: [avgLong, avgLat],
        zoom: 6,
        duration: 1000,
      });
    }
  }, [data]);

  // Fly to selected marker when selectedId changes
  useEffect(() => {
    if (!mapRef.current || !selectedId) return;

    const selectedItem = data.find((item) => item._id === selectedId);
    if (!selectedItem?.catchLocation) return;

    const location = parseLocation(selectedItem.catchLocation);
    if (location) {
      mapRef.current.flyTo({
        center: [location.longitude, location.latitude],
        zoom: 6,
        duration: 1000,
      });
    }
  }, [selectedId, data]);

  // Handle map resize on window resize
  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current) {
        mapRef.current.resize();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Guard against invalid data
  if (!Array.isArray(data) || data.length === 0) {
    return <div>No data available to display on the map.</div>;
  }

  return (
    <div className="map-view-container">
      <DataTable
        columns={columns}
        data={data}
        pagination
        highlightOnHover
        fixedHeader
        selectableRows
        selectableRowsSingle
        onSelectedRowsChange={handleSelectedRowsChange}
        selectedRows={selectedItems}
      />
      <Map
        ref={mapRef}
        initialViewState={viewport}
        style={{ width: '100%', height: 400, borderRadius: 5 }}
        mapStyle={mapStyle}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
      >
        {data.map((item) => {
          const location = parseLocation(item.catchLocation);
          if (!location) return null;

          const { latitude, longitude } = location;
          const isSelected = item._id === selectedId;

          return (
            <Marker
              key={item._id}
              latitude={latitude}
              longitude={longitude}
              anchor="bottom"
            >
              <img
                src={isSelected ? SelectedMarkerIcon : MarkerIcon}
                alt={`Marker for ${item.fishType || 'Unknown Vessel'}`}
                className={`marker-icon ${isSelected ? 'selected' : ''}`}
                onClick={() => handleMarkerClick(item)}
                style={{ cursor: 'pointer', width: 32, height: 32 }}
              />
            </Marker>
          );
        })}
        {selectedMarker && parseLocation(selectedMarker.catchLocation) && (
          <Popup
            latitude={parseLocation(selectedMarker.catchLocation).latitude}
            longitude={parseLocation(selectedMarker.catchLocation).longitude}
            anchor="top"
            closeOnClick={false}
            onClose={() => {
              setSelectedMarker(null);
              setSelectedId(null);
              setSelectedItems([]);
            }}
            offset={[0, -10]}
          >
            <div className="popup-content">
              <h4>{selectedMarker.fishType || 'Unknown Vessel'}</h4>
              <p>
                <strong>Latitude:</strong>{' '}
                {parseLocation(selectedMarker.catchLocation).latitude.toFixed(
                  6
                )}
              </p>
              <p>
                <strong>Longitude:</strong>{' '}
                {parseLocation(selectedMarker.catchLocation).longitude.toFixed(
                  6
                )}
              </p>
              <p>
                <strong>Date:</strong>{' '}
                {selectedMarker.catchDatetime
                  ? new Date(selectedMarker.catchDatetime).toLocaleDateString()
                  : 'N/A'}
              </p>
            </div>
          </Popup>
        )}
      </Map>
    </div>
  );
};

export default MapView;
